<template>
  <v-card class="pa-6">
    <v-card-title class="text-uppercase">
      {{ isEditing ? 'Editar clínica' : 'Nueva clínica Droopy' }}
    </v-card-title>
    <hr class="my-3" />
    <v-card-text>
      <v-form ref="clinicForm" v-model="formValid">
        <v-row class="my-1">
          <v-col cols="12">
            <v-text-field
              label="Nombre de la clínica"
              v-model="clinicForm.name"
              :rules="nameRules"
              required
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="my-1">
          <v-col cols="6">
            <v-text-field
              label="RUT"
              v-model="clinicForm.rut"
              :rules="rutRules"
              required
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <!-- Al editar es solo lectura: es el login de Cognito del administrador. -->
            <v-text-field
              label="Email del administrador"
              v-model="clinicForm.adminEmail"
              :rules="isEditing ? [] : emailRules"
              :disabled="isEditing"
              :required="!isEditing"
              outlined
              dense
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>
      <template v-if="isEditing && can('droopy-clinics:update-bank-details')">
        <v-divider class="my-4"></v-divider>
        <div v-if="loadingProfile" class="d-flex justify-center my-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <p v-else-if="profileLoadFailed" class="error--text text-body-2">
          No se pudieron cargar los datos de Red Pawer. Puedes guardar el nombre; para editar
          estos datos, cierra y vuelve a abrir el formulario.
        </p>
        <droopy-network-profile-form
          v-else
          ref="networkForm"
          v-model="networkForm"
          :missing="networkMissing"
          :disabled="saving"
        ></droopy-network-profile-form>
        <v-divider class="my-4"></v-divider>
        <clinic-bank-details-section
          :clinic-id="clinic.clinicId"
          :clinic-name="clinic.clinicName"
        ></clinic-bank-details-section>
      </template>
    </v-card-text>
    <hr class="my-3" />
    <v-card-actions class="justify-end">
      <v-btn text color="primary" rounded @click="closeDialog">Cancelar</v-btn>
      <v-btn
        v-if="can(isEditing ? 'droopy-clinics:update' : 'droopy-clinics:create')"
        :disabled="!formValid || saving"
        :loading="saving"
        color="primary"
        rounded
        @click="save"
      >
        {{ isEditing ? 'Guardar' : 'Crear cuenta de administrador' }}
      </v-btn>
    </v-card-actions>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import UsersService from '../services/users.service';
import ClinicsService from '../services/clinics.service';
import RutUtils from '../utils/rut.utils';
import { utilsMixin } from '../mixins/utilsMixin';
import ClinicBankDetailsSection from './ClinicBankDetailsSection.vue';
import DroopyNetworkProfileForm from './DroopyNetworkProfileForm.vue';
import {
  formatChileWhatsapp,
  formatMissingFields,
  toNetworkProfilePayload,
  toOpeningHoursForm
} from '../utils/red-pawer.utils';
import { can } from '../utils/permissions';

// El backend ya valida NAME_REQUIRED/RUT_REQUIRED/INVALID_RUT_FORMAT/ADMIN_EMAIL_REQUIRED
// (ver 063-clinic-admin-onboarding, pawer-backend) — este diccionario solo traduce esos
// códigos, mismo patrón que src/views/ClinicOnboarding.vue.
const CREATE_CLINIC_ERROR_MESSAGES = {
  NAME_REQUIRED: 'El nombre de la clínica es obligatorio.',
  RUT_REQUIRED: 'El RUT es obligatorio.',
  INVALID_RUT_FORMAT: 'El RUT ingresado no tiene un formato válido.',
  ADMIN_EMAIL_REQUIRED: 'El email del administrador es obligatorio.'
};

const NETWORK_PROFILE_ERROR_MESSAGES = {
  INVALID_WHATSAPP: 'El WhatsApp no es válido. Usa un celular chileno, por ejemplo +56 9 1234 5678.',
  INVALID_OPENING_HOURS: 'El horario de atención no es válido. Revisa que cada cierre sea después de la apertura.'
};

function emptyNetworkForm() {
  return {
    type: null,
    whatsapp: '',
    address: '',
    commune: null,
    homeVisitCommunes: [],
    openingHours: toOpeningHoursForm(null)
  };
}

// Códigos de PATCH /droopy-clinics/:clinicId (pawer-backend). CLINIC_RUT_TAKEN llega en `code`;
// el resto en `message`. Lo que no esté acá cae en el mensaje genérico.
const UPDATE_CLINIC_ERROR_MESSAGES = {
  CLINIC_RUT_TAKEN: 'Este RUT ya pertenece a otra clínica.',
  CLINIC_ADMIN_NOT_FOUND: 'Esta clínica no tiene una cuenta de administrador Droopy.',
  NAME_REQUIRED: 'El nombre de la clínica es obligatorio.',
  RUT_REQUIRED: 'El RUT es obligatorio.',
  INVALID_RUT_FORMAT: 'El RUT ingresado no tiene un formato válido.'
};

export default {
  name: 'DroopyClinicMaintainer',

  components: { ClinicBankDetailsSection, DroopyNetworkProfileForm },

  mixins: [utilsMixin],

  props: {
    clinic: { type: Object, default: null }
  },

  data() {
    return {
      formValid: false,
      saving: false,
      clinicForm: this.emptyForm(),
      networkForm: emptyNetworkForm(),
      networkMissing: [],
      loadingProfile: false,
      profileLoadFailed: false,
      nameRules: [v => !!v || 'El nombre es requerido'],
      // Al editar, el RUT ya guardado se acepta sin cambios (clínicas antiguas pueden tenerlo
      // fuera de formato); si se cambia, rigen las mismas reglas que al crear.
      rutRules: [
        v => !!v || 'El RUT es requerido',
        v => this.isOriginalRut(v) || RutUtils.validateRut(v) || 'RUT no válido'
      ],
      emailRules: [
        v => !!v || 'El email es requerido',
        v => /.+@.+\..+/.test(v) || 'Debe ser un email válido'
      ],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  computed: {
    isEditing() {
      return this.clinic !== null;
    }
  },

  watch: {
    clinic: {
      immediate: true,
      handler() {
        this.clinicForm = this.emptyForm();
        this.$nextTick(() => {
          if (this.$refs.clinicForm) this.$refs.clinicForm.resetValidation();
        });
        this.loadNetworkProfile();
      }
    }
  },

  methods: {
    can,
    emptyForm() {
      if (this.clinic) {
        return {
          name: this.clinic.clinicName ?? '',
          rut: RutUtils.formatRut(this.clinic.clinicRut ?? ''),
          adminEmail: this.clinic.email ?? ''
        };
      }
      return { name: '', rut: '', adminEmail: '' };
    },

    isOriginalRut(rut) {
      return (
        this.isEditing &&
        RutUtils.normalizeIdentifier(rut) === RutUtils.normalizeIdentifier(this.clinic.clinicRut)
      );
    },

    // Solo lo que cambió: el backend rechaza un RUT fuera de formato aunque sea el mismo.
    clinicChanges() {
      const changes = {};
      const name = this.clinicForm.name.trim();
      if (name !== this.clinic.clinicName) changes.name = name;
      if (!this.isOriginalRut(this.clinicForm.rut)) changes.rut = this.clinicForm.rut;
      return changes;
    },

    closeDialog() {
      this.$emit('close-dialog');
    },

    async save() {
      const networkValid = !this.$refs.networkForm || this.$refs.networkForm.validate();
      if (!this.$refs.clinicForm.validate() || !networkValid) return;

      this.saving = true;
      const response = this.isEditing ? await this.updateClinic() : await this.createClinic();
      if (response.status !== 'ok') {
        this.saving = false;
        this.showErrorSnackbar(
          this.isEditing ? this.mapUpdateClinicError(response) : this.mapCreateClinicError(response)
        );
        return;
      }

      const networkSaved = this.isEditing ? await this.saveNetworkProfile() : true;
      this.saving = false;
      if (networkSaved) this.closeDialog();
    },

    async loadNetworkProfile() {
      this.networkForm = emptyNetworkForm();
      this.networkMissing = [];
      this.profileLoadFailed = false;
      this.loadingProfile = false;
      if (!this.clinic) return;

      const clinicId = this.clinic.clinicId;
      this.loadingProfile = true;
      const res = await ClinicsService.getNetworkProfile(clinicId);
      // El diálogo pudo cambiar de clínica mientras cargaba.
      if (!this.clinic || this.clinic.clinicId !== clinicId) return;
      if (res.status === 'ok') {
        this.networkForm = this.toNetworkForm(res.data);
      } else {
        this.profileLoadFailed = true;
      }
      this.loadingProfile = false;
    },

    toNetworkForm(profile) {
      const data = profile || {};
      return {
        type: data.type ?? null,
        whatsapp: formatChileWhatsapp(data.whatsapp),
        address: data.address ?? '',
        commune: data.commune ?? null,
        homeVisitCommunes: data.homeVisitCommunes ?? [],
        openingHours: toOpeningHoursForm(data.openingHours)
      };
    },

    // Devuelve true si se puede cerrar el diálogo. Sin tipo no hay perfil que guardar
    // (el PUT exige 'clinic' o 'home_visit').
    async saveNetworkProfile() {
      if (this.profileLoadFailed || !this.networkForm.type) return true;

      const res = await ClinicsService.updateNetworkProfile(
        this.clinic.clinicId,
        toNetworkProfilePayload(this.networkForm)
      );
      if (res.status !== 'ok') {
        this.showErrorSnackbar(
          NETWORK_PROFILE_ERROR_MESSAGES[res.code] ||
            'Se guardaron los datos de la clínica, pero no los de Red Pawer. Intenta nuevamente.'
        );
        return false;
      }

      const missing = (res.data && res.data.missing) || [];
      if (missing.length) {
        // El backend la ocultó de la app: se deja el diálogo abierto para completar.
        this.networkMissing = missing;
        this.showWarningSnackbar(
          `Se guardaron los cambios, pero la clínica dejó de mostrarse en la app. Falta: ${formatMissingFields(missing)}.`
        );
        return false;
      }
      return true;
    },

    async createClinic() {
      return UsersService.createManualUser('clinic', { ...this.clinicForm });
    },

    // Solo se llama al endpoint de la clínica si cambió el nombre o el RUT: falla con 404 en
    // cuentas sin usuario de Droopy (sin email), y eso no debe bloquear guardar los datos de
    // Red Pawer.
    async updateClinic() {
      const changes = this.clinicChanges();
      if (Object.keys(changes).length === 0) return { status: 'ok' };
      return ClinicsService.updateDroopyClinic(this.clinic.clinicId, changes);
    },

    mapCreateClinicError(response) {
      if (response.httpCode === 409) {
        return 'Esta clínica ya tiene una cuenta de administrador.';
      }
      return (
        CREATE_CLINIC_ERROR_MESSAGES[response.message] ||
        'Ocurrió un error al crear la clínica. Intenta nuevamente.'
      );
    },

    mapUpdateClinicError(response) {
      return (
        UPDATE_CLINIC_ERROR_MESSAGES[response.code] ||
        UPDATE_CLINIC_ERROR_MESSAGES[response.message] ||
        'Ocurrió un error al actualizar la clínica. Intenta nuevamente.'
      );
    }
  }
};
</script>
